import { format } from 'owa-localize';

import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { createRemoveRecipientLink } from './createRemoveRecipientLinks';
import RemoveMultiRecipientMailtip from '../components/RemoveMultiRecipientMailtip';
import * as React from 'react';
import type { MessagePart } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import type { MailTipsCategory } from '../datapoints';

/**
 * @param recipient
 * @param str
 * @param onRemoveRecipientsClick
 * returns a formatted string and remove link for a single recipient
 */
export const getMessagePartsForSingleRecipient = (
    recipient: EmailAddressWrapper,
    str: string,
    onRemoveRecipientsClick?: (recipients: EmailAddressWrapper[]) => void,
    mailTipsCategory?: MailTipsCategory
): [string, MessagePart] => [
    format(str, recipient.Name),
    onRemoveRecipientsClick
        ? createRemoveRecipientLink(recipient, onRemoveRecipientsClick, mailTipsCategory)
        : undefined,
];

/**
 * @param recipients
 * @param singleRecipientStr
 * @param multiRecipientStr
 * @param onRemoveRecipientsClick
 * returns messageParts in case of 1 recipient with messageElement as null.
 * returns messageElement for more than 1 recipients with messageParts as null.
 * returns messageString, the string equivalent of the content of the infobar
 */
export const getGenericMessageElementAndParts = (
    recipients: EmailAddressWrapper[],
    singleRecipientStr: string,
    multiRecipientStr: string,
    onRemoveRecipientsClick?: (recipients: EmailAddressWrapper[]) => void,
    mailTipsCategory?: MailTipsCategory
): [JSX.Element, null, string] | [null, MessagePart[], string] => {
    const messageParts =
        recipients.length === 1
            ? getMessagePartsForSingleRecipient(
                  recipients[0],
                  singleRecipientStr,
                  onRemoveRecipientsClick,
                  mailTipsCategory
              )
            : null;

    if (!messageParts) {
        const messageInfoText = format(multiRecipientStr, recipients.length);
        return [
            // Not being rendered here. Don't insert this as an arrayed element
            // in a react tree.
            //
            // tslint:disable-next-line:jsx-key
            <RemoveMultiRecipientMailtip
                messageInfoText={messageInfoText}
                recipientEmails={recipients}
                onRemove={onRemoveRecipientsClick}
                mailTipsCategory={mailTipsCategory}
            />,
            null,
            messageInfoText,
        ];
    } else {
        return [null, messageParts, messageParts[0]];
    }
};
